/**
 * Copyright 2025 Lumavec Group LLC
 *
 * All rights reserved. No part of this software may be used, copied, modified,
 * or distributed without explicit written permission. This software is provided
 * "as-is" without warranty of any kind, express or implied, including but not
 * limited to warranties of merchantability or fitness for a particular purpose.
 */

import { pgTable, text, serial, integer, timestamp, jsonb, boolean } from "drizzle-orm/pg-core";
import { createInsertSchema } from "drizzle-zod";
import { z } from "zod";

// Assessment item types
export const ASSESSMENT_ITEM_TYPES = {
  MULTIPLE_CHOICE_SINGLE: 'multiple_choice_single',
  MULTIPLE_CHOICE_MULTI: 'multiple_choice_multi',
  FILL_IN_BLANK: 'fill_in_blank',
  SHORT_ANSWER: 'short_answer',
  LONG_ANSWER: 'long_answer',
} as const;

export type AssessmentItemType = typeof ASSESSMENT_ITEM_TYPES[keyof typeof ASSESSMENT_ITEM_TYPES];

// Assessment rubrics
export const rubrics = pgTable("rubrics", {
  id: serial("id").primaryKey(),
  name: text("name").notNull(),
  description: text("description"),
  criteria: jsonb("criteria").notNull(), // Array of criteria: [{name: "Logic", description: "Logical reasoning", levels: [{level: "Excellent", points: 5, description: "Clear logical flow"}, {level: "Good", points: 4, description: "Mostly logical"}, ...]}, ...]
  maxPoints: integer("max_points").notNull(), // Total maximum points possible
  skillIds: integer("skill_ids").array(), // Skills this rubric evaluates
  topicIds: integer("topic_ids").array(), // Topics this rubric evaluates
  isPublic: boolean("is_public").default(false), // Whether this rubric can be shared/reused
  createdById: integer("created_by_id"), // User who created this rubric
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
});

// Assessment item banks
export const itemBanks = pgTable("item_banks", {
  id: serial("id").primaryKey(),
  name: text("name").notNull(),
  description: text("description"),
  itemCount: integer("item_count").default(0),
  skillIds: integer("skill_ids").array(),
  topicIds: integer("topic_ids").array(),
  metadata: jsonb("metadata"), // Generation metadata and other relevant information
  createdAt: timestamp("created_at").defaultNow(),
});

// Assessment items
export const assessmentItems = pgTable("assessment_items", {
  id: serial("id").primaryKey(),
  itemBankId: integer("item_bank_id").notNull(),
  type: text("type").notNull(), // from ASSESSMENT_ITEM_TYPES
  question: text("question").notNull(),
  options: jsonb("options"), // For multiple choice: [{id: 1, text: "Option 1", correct: true}, ...]
  correctAnswer: text("correct_answer"), // For fill in blank and short answer
  goldenAnswer: text("golden_answer"), // Reference answer used for automatic scoring with LLM
  explanation: text("explanation"), // Explanation of the correct answer
  rubricId: integer("rubric_id"), // Reference to rubrics table for subjective assessments
  rubric: jsonb("rubric"), // For legacy/inline rubrics: {criteria: [{name: "Logic", points: 5, description: "..."}, ...]}
  difficultyLevel: integer("difficulty_level"), // 1-5 scale
  skillIds: integer("skill_ids").array(),
  topicIds: integer("topic_ids").array(),
  metadata: jsonb("metadata"), // Generation metadata and other relevant information
  createdAt: timestamp("created_at").defaultNow(),
});

// Assessments (collections of assessment items)
export const assessments = pgTable("assessments", {
  id: serial("id").primaryKey(),
  name: text("name").notNull(),
  description: text("description"),
  type: text("type"),
  status: text("status").default('draft'),
  itemCount: integer("item_count").default(0),
  difficulty: text("difficulty"),
  questions: integer("questions").array().notNull(), // Array of assessment item IDs, stored as integer array
  contentId: integer("content_id"),
  metadata: jsonb("metadata"), // Generation metadata and other relevant information
});

// Insert schemas
export const insertRubricSchema = createInsertSchema(rubrics).omit({ id: true, createdAt: true, updatedAt: true });
export const insertItemBankSchema = createInsertSchema(itemBanks).omit({ id: true, createdAt: true });
export const insertAssessmentItemSchema = createInsertSchema(assessmentItems).omit({ id: true, createdAt: true });
export const insertAssessmentSchema = createInsertSchema(assessments).omit({ id: true });

// Assessment Item Scores - individual responses to assessment items
export const assessmentItemScores = pgTable("assessment_item_scores", {
  id: serial("id").primaryKey(),
  assessmentItemId: integer("assessment_item_id").notNull(),
  userId: integer("user_id").notNull(),
  studentAnswer: text("student_answer").notNull(),
  contentAccuracyScore: integer("content_accuracy_score"), // 0-100
  clarityScore: integer("clarity_score"), // 0-100
  communicationStyleScore: integer("communication_style_score"), // 0-100
  voiceQualityScore: integer("voice_quality_score"), // 0-100
  overallQuestionScore: integer("overall_question_score"), // 0-100
  feedback: text("feedback"),
  notes: text("notes"),
  createdAt: timestamp("created_at").defaultNow(),
});

// Assessment Scores - overall assessment results
export const assessmentScores = pgTable("assessment_scores", {
  id: serial("id").primaryKey(),
  assessmentId: integer("assessment_id").notNull(),
  userId: integer("user_id").notNull(),
  assessmentItemScoreIds: integer("assessment_item_score_ids").array().notNull(),
  overallContentScore: integer("overall_content_score"), // 0-100
  overallCommunicationScore: integer("overall_communication_score"), // 0-100
  overallAssessmentScore: integer("overall_assessment_score"), // 0-100
  strengths: text("strengths").array(), // Array of strings
  areasForImprovement: text("areas_for_improvement").array(), // Array of strings
  finalFeedback: text("final_feedback"),
  recommendedNextSteps: text("recommended_next_steps"),
  gradeLetter: text("grade_letter"),
  createdAt: timestamp("created_at").defaultNow(),
  completedAt: timestamp("completed_at"),
});

// Insert schemas
export const insertAssessmentItemScoreSchema = createInsertSchema(assessmentItemScores).omit({ id: true, createdAt: true });
export const insertAssessmentScoreSchema = createInsertSchema(assessmentScores).omit({ id: true, createdAt: true });

// User Assessments - tracks user progress through assessments
export const userAssessments = pgTable("user_assessments", {
  id: serial("id").primaryKey(),
  userId: integer("user_id").notNull(),
  assessmentId: integer("assessment_id").notNull(),
  status: text("status").default('in_progress'), // 'in_progress', 'completed', 'abandoned'
  score: integer("score").default(0),
  maxScore: integer("max_score").default(100),
  startedAt: timestamp("started_at").defaultNow(),
  completedAt: timestamp("completed_at"),
  attemptNumber: integer("attempt_number").default(1),
  responses: jsonb("responses"), // JSON storing user responses
  feedback: text("feedback"),
});

export const insertUserAssessmentSchema = createInsertSchema(userAssessments).omit({ id: true, startedAt: true });

// Types
export type Rubric = typeof rubrics.$inferSelect;
export type InsertRubric = z.infer<typeof insertRubricSchema>;
export type ItemBank = typeof itemBanks.$inferSelect;
export type InsertItemBank = z.infer<typeof insertItemBankSchema>;
export type AssessmentItem = typeof assessmentItems.$inferSelect;
export type InsertAssessmentItem = z.infer<typeof insertAssessmentItemSchema>;
export type Assessment = typeof assessments.$inferSelect;
export type InsertAssessment = z.infer<typeof insertAssessmentSchema>;
export type AssessmentItemScore = typeof assessmentItemScores.$inferSelect;
export type InsertAssessmentItemScore = z.infer<typeof insertAssessmentItemScoreSchema>;
export type AssessmentScore = typeof assessmentScores.$inferSelect;
export type InsertAssessmentScore = z.infer<typeof insertAssessmentScoreSchema>;
export type UserAssessment = typeof userAssessments.$inferSelect;
export type InsertUserAssessment = z.infer<typeof insertUserAssessmentSchema>; 